import { attempt } from "@omni/utils/function.utils";
import * as LocalStorage from "@omni/utils/localStorage.utils";
import * as SessionStorage from "@omni/utils/sessionStorage.utils";

import { add, isAfter } from "@/utils/date";
import { StorageKeys } from "@/utils/enums";
import { uuidV4 } from "@/utils/uuid";

export enum CallServiceKey {
    Cart = "CART",
    Wishlist = "WISHLIST",
    Customer = "CUSTOMER"
}

type ShouldCallSessionPayload = {
    shouldLoad: boolean;
    timestamp: number;
    uuid?: string;
};

type ShouldCallLocalPayload = {
    timestamp: number;
    uuid?: string;
};

export default class CallService {
    private static makeKey(key: CallServiceKey) {
        return StorageKeys.ShouldCall + key;
    }

    private static getSessionItem(generatedKey: string) {
        const sessionItem = SessionStorage.getItem(generatedKey);
        return attempt<ShouldCallSessionPayload | undefined>(
            () => (sessionItem ? JSON.parse(sessionItem) : undefined),
            undefined
        );
    }

    private static getLocalItem(generatedKey: string) {
        const localItem = LocalStorage.getItem(generatedKey);
        return attempt<ShouldCallLocalPayload | undefined>(
            () => (localItem ? JSON.parse(localItem) : undefined),
            undefined
        );
    }

    private static getUUID(generatedKey: string) {
        const sessionItem = CallService.getSessionItem(generatedKey);
        return sessionItem?.uuid || uuidV4();
    }

    private static setItem(key: CallServiceKey, shouldLoad: boolean) {
        const generatedKey = CallService.makeKey(key);
        const uuid = CallService.getUUID(generatedKey);
        const payload = JSON.stringify({
            shouldLoad,
            uuid,
            timestamp: Date.now()
        });

        SessionStorage.setItem(generatedKey, payload);
        LocalStorage.setItem(generatedKey, payload);
    }

    static enable(key: CallServiceKey) {
        CallService.setItem(key, true);
    }

    static disable(key: CallServiceKey) {
        CallService.setItem(key, false);
    }

    static shouldCall(key: CallServiceKey, retention: Duration): boolean {
        const generatedKey = CallService.makeKey(key);
        const sessionItem = CallService.getSessionItem(generatedKey);
        const localItem = CallService.getLocalItem(generatedKey);

        let result = true;

        if (sessionItem) {
            const hasTimedOut = isAfter(
                new Date(),
                add(new Date(sessionItem.timestamp), retention)
            );

            if (!sessionItem.shouldLoad) {
                result = hasTimedOut;
            }
        }

        if (
            sessionItem &&
            !result &&
            localItem?.uuid &&
            localItem?.uuid !== sessionItem?.uuid
        ) {
            const hasTimeout = isAfter(
                new Date(localItem.timestamp),
                new Date(sessionItem.timestamp)
            );

            if (hasTimeout) {
                result = true;
            }
        }

        return result;
    }
}
